import { useVirtualizer } from '@tanstack/react-virtual';
import * as React from 'react';
import type { Omittable } from "../../lib/props";

export type UseDataTableVirtualizerOptions = Omittable<{
  /** Number of items to virtualize */
  count: number;
  /** Fixed row height in pixels (from density config) */
  rowHeight: number;
  /** Ref to the shared outer scroll container */
  scrollContainerRef: React.RefObject<unknown>;
  /** Offset from top of scroll container to this group's body */
  scrollMargin?: number;
  /** Extra items to render outside the visible area */
  overscan?: number;
  /** Whether the virtualizer is active */
  enabled?: boolean;
}>;

/**
 * Virtualizer hook for DataTable groups.
 *
 * Wraps TanStack's useVirtualizer to provide virtual scrolling.
 *
 * Each group gets its own virtualizer instance. The scrollMargin option
 * tells the virtualizer where this group's body starts relative to the
 * shared outer scroll container.
 */
export function useDataTableVirtualizer({
  count,
  rowHeight,
  scrollContainerRef,
  scrollMargin = 0,
  overscan = 10,
  enabled = true,
}: UseDataTableVirtualizerOptions) {
  const isActive = enabled;

  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => {
      if (!isActive) return null;
      return (scrollContainerRef.current as Element) ?? null;
    },
    estimateSize: () => rowHeight,
    overscan,
    scrollMargin,
    enabled: isActive,
    // Disable flushSync to prevent "flushSync was called from inside a
    // lifecycle method" errors. React batches updates naturally without it.
    // See: https://github.com/TanStack/virtual/issues/1094
    useFlushSync: false,
  });

  // With useFlushSync disabled, the ResizeObserver-triggered rerender({})
  // can be swallowed by React 18's automatic batching during route
  // transitions. Schedule a measure() call in rAF as a reliable fallback:
  // ResizeObserver callbacks fire *before* rAF in the browser event loop,
  // so by the time this runs the virtualizer's internal scrollRect is set.
  // measure() then triggers rerender({}) outside the transition batch.
  const hasMeasuredRef = React.useRef(false);
  React.useEffect(() => {
    if (!isActive) {
      hasMeasuredRef.current = false;
      return;
    }
    if (hasMeasuredRef.current) return;
    const id = requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        hasMeasuredRef.current = true;
        virtualizer.measure();
      }
    });
    return () => cancelAnimationFrame(id);
    // Re-run when activation state or count changes (initial mount / group expand).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, count]);

  return isActive ? virtualizer : null;
}
